'use client';

import ActivityProductGrid, {
  ActivityProductsData,
} from '@/app/(activity)/components/ActivityProductGrid';
import FaqAccordion from '@/app/(activity)/components/FaqAccordion';
import HotProductSection from '@/app/(activity)/components/HotProductSection';
import { usePageCache } from '@/hooks/usePageCache';
import { useStore } from '@/providers/app-store-provider';
import '@/styles/animations.css';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useEffect } from 'react';
import StaticFaqForSeo from '../../components/StaticFaqForSeo';
import { useBlueLightGlassesTracking } from '../hooks/useBlueLightGlassesTracking';
import '../styles/blue-light-glasses.css';
import BlueLightAdvantages from './BlueLightAdvantages';
import BlueLightBanner from './BlueLightBanner';
import BlueLightEffects from './BlueLightEffects';
import BlueLightHowBuy from './BlueLightHowBuy';
import BlueLightSceneSupport from './BlueLightSceneSupport';

interface Props {
  blueLightData: any;
  deviceType: EPlatform;
  hotProducts: any[];
  activityProductsData: ActivityProductsData;
}

export default function BlueLightGlassesContent({
  blueLightData,
  deviceType,
  hotProducts,
  activityProductsData,
}: Props) {
  const isMobile = deviceType !== EPlatform.WEB;

  // App相关状态
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);
  // const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 使用埋点hooks
  const {
    trackKnowledgeTopicPageView,
    trackKnowledgeTopicPageBannerExp,
    trackKnowledgeTopicPageBannerClick,
    trackKnowledgeTopicPageMktExp,
    trackKnowledgeTopicPageProductExp,
    trackKnowledgeTopicPageProductClick,
  } = useBlueLightGlassesTracking();

  // 使用页面缓存 hook 来保存页面状态和控制动画
  const { pageState, isFromCache } = usePageCache({
    pageKey: 'blue-light-blocking-glasses-page-v1',
    initialData: {
      blueLightData,
      deviceType,
      hotProducts,
      activityProductsData,
      timestamp: Date.now(),
    },
  });

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
      window.addEventListener('appView', trackKnowledgeTopicPageView);
      return () => {
        window.removeEventListener('appView', trackKnowledgeTopicPageView);
      };
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 页面浏览埋点 - 组件加载时触发
  useEffect(() => {
    trackKnowledgeTopicPageView();
  }, [trackKnowledgeTopicPageView]);

  // 组件卸载时清理动画 - 对应Vue中的removeAnimation，只在PC端执行
  React.useEffect(() => {
    // 只在PC端添加动画相关逻辑
    if (isMobile) return;

    return () => {
      try {
        const animationClasses = [
          'blueTitleFadeInUpAnimate',
          'fadeInUpAnimate',
          'fadeInLeftAnimate',
          'fadeInRightAnimate',
          'activityFadeInUpAnimate',
          'activityFadeInLeftAnimate',
          'activityFadeInRightAnimate',
          'opacity0',
          'hasAnimate',
        ];

        animationClasses.forEach((className) => {
          const elements = document.querySelectorAll(`.${className}`);
          elements.forEach((element) => {
            element.classList.remove(className);
            if (element.classList.contains('opacity0')) {
              element.classList.remove('opacity0');
            }
            const htmlElement = element as HTMLElement;
            htmlElement.style.transform = 'translateY(0)';
            htmlElement.style.opacity = '1';
          });
        });
      } catch {
        // 不做处理
      }
    };
  }, [isMobile]);

  return (
    <>
      <div
        className={`w-full min-h-screen ${isMobile ? 'blue-light-wap' : 'blue-light-pc'}`}
        style={{ backgroundColor: '#01000e' }}
      >
        {/* Banner 模块 */}
        <BlueLightBanner
          bannerData={pageState.blueLightData.banner}
          onBannerExp={(params) => {
            trackKnowledgeTopicPageBannerExp({ group_id: params.group });
          }}
          onBannerClick={(params) => {
            trackKnowledgeTopicPageBannerClick({ group_id: params.group });
          }}
        />

        {/* 防蓝光效果模块 */}
        <BlueLightEffects
          effectData={pageState.blueLightData.blueLight_effects}
          deviceType={deviceType}
          isFromCache={isFromCache}
          onSectionExp={() => {
            trackKnowledgeTopicPageMktExp({
              mkt_name: '防蓝光的影响',
            });
          }}
        />

        {/* PC端渲染顺序 */}
        {!isMobile && (
          <>
            {/* 热卖产品模块 */}
            <HotProductSectionLocal
              hotProduct={pageState.blueLightData.hot_product}
              deviceType={deviceType}
              hotProducts={pageState.hotProducts}
              isFromCache={isFromCache}
              trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
              trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
            />

            {/* 优势模块 */}
            <BlueLightAdvantages
              advantagesData={pageState.blueLightData.blueLight_advantages}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '防蓝光眼镜优势',
                });
              }}
            />

            {/* 场景支持模块 */}
            <BlueLightSceneSupport
              supportData={pageState.blueLightData.scene_support}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '各种场景支持',
                });
              }}
            />

            {/* 如何购买模块 */}
            <BlueLightHowBuy
              howBuyData={pageState.blueLightData.how_buy}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '如何购买',
                });
              }}
            />

            {/* 商品列表部分 */}
            <ProductListSection
              listPart={pageState.blueLightData.list_part}
              activityProductsData={pageState.activityProductsData}
              deviceType={deviceType}
              trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
              trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
            />

            {/* FAQ 部分 - PC端 */}
            {pageState.blueLightData.faq_part && (
              <FaqAccordion
                faqData={{
                  title: pageState.blueLightData.faq_part.title || 'FAQ',
                  data_array: pageState.blueLightData.faq_part.faq_list || [],
                }}
                className="!bg-white !text-black"
                onExposure={() => {
                  trackKnowledgeTopicPageMktExp({
                    mkt_name: 'FAQ',
                  });
                }}
              />
            )}
            <StaticFaqForSeo
              faqData={{
                title: pageState.blueLightData.faq_part.title || 'FAQ',
                data_array: pageState.blueLightData.faq_part.faq_list || [],
              }}
              className="static-faq"
            />
          </>
        )}

        {/* WAP端渲染顺序 */}
        {isMobile && (
          <>
            {/* 热卖产品模块 */}
            <HotProductSectionLocal
              hotProduct={pageState.blueLightData.hot_product}
              deviceType={deviceType}
              hotProducts={pageState.hotProducts}
              isFromCache={isFromCache}
              trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
              trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
            />

            {/* 优势模块 */}
            <BlueLightAdvantages
              advantagesData={pageState.blueLightData.blueLight_advantages}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '防蓝光眼镜优势',
                });
              }}
            />

            {/* 场景支持模块 */}
            <BlueLightSceneSupport
              supportData={pageState.blueLightData.scene_support}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '各种场景支持',
                });
              }}
            />

            {/* FAQ 部分 - 移动端 */}
            {pageState.blueLightData.faq_part && (
              <FaqAccordion
                faqData={{
                  title: pageState.blueLightData.faq_part.title || 'FAQ',
                  data_array: pageState.blueLightData.faq_part.faq_list || [],
                }}
                className="!text-white blue-light-faq"
                titleClassName="font-bb text-[20px] leading-[24px] text-center text-white border-b border-white/30 pb-[16px]"
                onExposure={() => {
                  trackKnowledgeTopicPageMktExp({
                    mkt_name: 'FAQ',
                  });
                }}
              />
            )}

            {/* 如何购买模块 */}
            <BlueLightHowBuy
              howBuyData={pageState.blueLightData.how_buy}
              deviceType={deviceType}
              isFromCache={isFromCache}
              onSectionExp={() => {
                trackKnowledgeTopicPageMktExp({
                  mkt_name: '如何购买',
                });
              }}
            />

            {/* 商品列表部分 */}
            <ProductListSection
              listPart={pageState.blueLightData.list_part}
              activityProductsData={pageState.activityProductsData}
              deviceType={deviceType}
              trackKnowledgeTopicPageProductExp={trackKnowledgeTopicPageProductExp}
              trackKnowledgeTopicPageProductClick={trackKnowledgeTopicPageProductClick}
            />
          </>
        )}
      </div>
    </>
  );
}

// 热卖产品模块 - 使用通用组件
function HotProductSectionLocal({
  hotProduct,
  deviceType,
  hotProducts,
  isFromCache,
  trackKnowledgeTopicPageProductExp,
  trackKnowledgeTopicPageProductClick,
}: {
  hotProduct: any;
  deviceType: EPlatform;
  hotProducts: any[];
  isFromCache: boolean;
  trackKnowledgeTopicPageProductExp: (params: any) => void;
  trackKnowledgeTopicPageProductClick: (params: any) => void;
}) {
  if (!hotProduct?.group) {
    return null;
  }

  const handleProductClick = (product: any, index: number) => {
    trackKnowledgeTopicPageProductClick({
      item: product,
      index,
      mkt_name: '热卖商品位',
    });
  };

  const handleProductExp = (product: any, index: number) => {
    trackKnowledgeTopicPageProductExp({
      item: product,
      index,
      mkt_name: '热卖商品位',
    });
  };

  return (
    <HotProductSection
      hotProduct={hotProduct}
      hotProducts={hotProducts}
      deviceType={deviceType}
      isFromCache={isFromCache}
      theme="blueLight"
      onProductClick={handleProductClick}
      onProductExp={handleProductExp}
    />
  );
}

// 商品列表模块
function ProductListSection({
  listPart,
  deviceType,
  trackKnowledgeTopicPageProductExp,
  trackKnowledgeTopicPageProductClick,
  activityProductsData,
}: {
  listPart: any;
  deviceType: EPlatform;
  trackKnowledgeTopicPageProductExp: (params: any) => void;
  trackKnowledgeTopicPageProductClick: (params: any) => void;
  activityProductsData?: ActivityProductsData;
}) {
  if (!listPart?.group) {
    return null;
  }

  const isMobile = deviceType !== EPlatform.WEB;

  return (
    <div className="bg-[#ffffff]">
      <ActivityProductGrid
        id="more-blue-light-glasses"
        title={listPart.title || 'More Blue Light Blocking Glasses'}
        apiConfig={{
          group_id: listPart.group,
          limit: 12, // 更多商品数量
        }}
        staticProducts={activityProductsData.list}
        staticTotal={activityProductsData.total}
        showSort
        // 移动端使用无限滚动，PC端使用分页器
        showInfiniteScroll={isMobile}
        showPagination={!isMobile}
        deviceType={deviceType}
        wapColumns={2}
        pcColumns={{ lg: 3, xl: 4, '2xl': 4 }}
        padding={{
          wap: 'px-[14px]',
          pc: 'px-[48px] lg:px-[100px]',
        }}
        stickyTop={{
          wap: 'top-[11.733vw]', // WAP端保持默认
          pc: 'top-[60px]', // PC端设置为60px
        }}
        productCardProps={{
          showTryOn: false,
          showCorrelation: true,
          showReviewStar: true,
          className:
            deviceType !== EPlatform.WEB
              ? 'bg-none'
              : 'bg-white rounded-lg hover:shadow-lg transition-shadow',
          imageClassName: deviceType !== EPlatform.WEB ? 'h-[176px] rounded-[12px]' : undefined,
        }}
        onProductClick={(product, index) => {
          trackKnowledgeTopicPageProductClick({
            item: product,
            index,
            mkt_name: '商品位',
          });
        }}
        onProductExp={(product, index) => {
          trackKnowledgeTopicPageProductExp({
            item: product,
            index,
            mkt_name: '商品位',
          });
        }}
      />
    </div>
  );
}
